import {
    IAnyModelType,
    IStateTreeNode,
    Instance,
    applyPatch,
    applySnapshot,
    castToSnapshot,
    getSnapshot,
    getType,
    isStateTreeNode,
    onSnapshot,
    types
} from "mobx-state-tree";
import { use } from "react";

import { AuthSettingStore } from "./AuthSettingStore";
import { ControlSettingStore } from "./ControlSettingStore";
import { OpsGenieAppSetting } from "Apps/OpsGenie/OpsGenieAppSetting";
import { SiteThemeStore } from "./SiteThemeStore";
import { TimeStore } from "./TimeStore";
import { UserAppHubSettings } from "Apps/AppHub/Store/UserAppHubSettings";
import { UserDashboardSettingsStore } from "Apps/Dashboard/Components/UserDashboardSettingsStore";
import { UserSearchSettings } from "./UserSearchSettings";
import { useRootStore } from "./RootStore";
import { LazyAppSettingKey, LazyAppSettingType, appSettingLoaders, lazyAppSettingKeys } from "./AppSettingLoaders";

/**
 * The saved settings of an app whose settings store loads with the app (see AppSettingLoaders.ts). The slot keeps the saved
 * snapshot as it is, so saving keeps it while the app isn't open; once the app's store loads, it writes its snapshot back here.
 */
const lazyAppSetting = types.frozen<unknown>();

// Property order is the saved JSON's key order; keep it when adding settings.
const globalSettingProperties = {
    time: types.optional(TimeStore, {}),
    theme: types.optional(SiteThemeStore, {}),
    auth: types.optional(AuthSettingStore, {}),
    controlSetting: types.optional(ControlSettingStore, {}),
    corpIdentityApp: lazyAppSetting,
    umsApp: lazyAppSetting,
    // The shell (navigation, layout) uses these three at startup, so they stay in the startup bundle.
    dashboardApp: types.optional(UserDashboardSettingsStore, {}),
    customerHubApp: lazyAppSetting,
    appHubApp: types.optional(UserAppHubSettings, {}),
    teamsApp: lazyAppSetting,
    opsGenieApp: types.optional(OpsGenieAppSetting, {}),
    ic3SpogApp: lazyAppSetting,
    ic3IEGApp: lazyAppSetting,
    contactsApp: lazyAppSetting,
    runtimeInsightsApp: lazyAppSetting,
    rulesApp: lazyAppSetting,
    mpaApp: lazyAppSetting,
    signalHealthApp: lazyAppSetting,
    deploymentHubApp: lazyAppSetting,
    sovCloudOpsApp: lazyAppSetting,
    statusFranceApp: lazyAppSetting,
    recoveryControlPanelApp: lazyAppSetting,
    TorusWebDMSSettings: lazyAppSetting,
    transportQueueApp: lazyAppSetting,
    userSearchSettings: types.optional(UserSearchSettings, {}),
    r9UserSettings: lazyAppSetting,
    sarcApp: lazyAppSetting,
    agencyHubApp: lazyAppSetting
};

type GlobalSettingProperties = typeof globalSettingProperties;
type IsLazySlot<T> = [T] extends [typeof lazyAppSetting] ? ([typeof lazyAppSetting] extends [T] ? true : false) : false;
type LazySlotKey = { [K in keyof GlobalSettingProperties]: IsLazySlot<GlobalSettingProperties[K]> extends true ? K : never }[keyof GlobalSettingProperties];
type AssertTrue<T extends true> = T;
/** Fails to compile when the lazy slots above and the loaders in AppSettingLoaders.ts don't list the same keys. */
export type LazySlotsMatchLoaders = AssertTrue<[LazySlotKey] extends [LazyAppSettingKey] ? ([LazyAppSettingKey] extends [LazySlotKey] ? true : false) : false>;

export const GlobalSettingStore = types.model("globalSettingStore", globalSettingProperties);

export type GlobalSettingInstance = Instance<typeof GlobalSettingStore>;
export type storeNames = keyof GlobalSettingInstance & string;

const isLazyAppSettingKey = (key: string): key is LazyAppSettingKey => Object.prototype.hasOwnProperty.call(appSettingLoaders, key);

const loadedTypes = new Map<LazyAppSettingKey, IAnyModelType>();
const typeLoads = new Map<LazyAppSettingKey, Promise<IAnyModelType>>();
const appSettingsByStore = new WeakMap<GlobalSettingInstance, Map<LazyAppSettingKey, IStateTreeNode>>();
const appSettingLoadsByStore = new WeakMap<GlobalSettingInstance, Map<LazyAppSettingKey, Promise<IStateTreeNode>>>();

const getOrAdd = <K, V>(map: Map<K, V>, key: K, create: () => V): V => {
    const existing = map.get(key);
    if (existing !== undefined) {
        return existing;
    }
    const created = create();
    map.set(key, created);
    return created;
};

const getAppSettings = (store: GlobalSettingInstance) => {
    let appSettings = appSettingsByStore.get(store);
    if (!appSettings) {
        appSettings = new Map();
        appSettingsByStore.set(store, appSettings);
    }
    return appSettings;
};

const getAppSettingLoads = (store: GlobalSettingInstance) => {
    let loads = appSettingLoadsByStore.get(store);
    if (!loads) {
        loads = new Map();
        appSettingLoadsByStore.set(store, loads);
    }
    return loads;
};

const loadAppSettingType = (key: LazyAppSettingKey) =>
    getOrAdd(typeLoads, key, async () => {
        try {
            const type: IAnyModelType = await appSettingLoaders[key].load();
            loadedTypes.set(key, type);
            return type;
        } catch (error) {
            // A failed load isn't kept, so the next use tries again.
            typeLoads.delete(key);
            throw error;
        }
    });

// As when the whole store loads: a saved snapshot that no longer fits the app's model gives way to the model's defaults.
const toAppSnapshot = (type: IAnyModelType, saved: unknown): unknown => (saved !== undefined && type.is(saved) ? saved : {});

const createAppSetting = (store: GlobalSettingInstance, key: LazyAppSettingKey, type: IAnyModelType): IStateTreeNode => {
    const appSetting: unknown = type.create(toAppSnapshot(type, store[key]));
    if (!isStateTreeNode(appSetting)) {
        throw new Error(`Settings store for "${key}" isn't a state tree node`);
    }
    onSnapshot(appSetting, () => {
        applyPatch(store, { op: "replace", path: `/${key}`, value: getSnapshot(appSetting) });
    });
    getAppSettings(store).set(key, appSetting);
    return appSetting;
};

const getLoadedAppSetting = (store: GlobalSettingInstance, key: LazyAppSettingKey): IStateTreeNode | undefined => {
    const existing = getAppSettings(store).get(key);
    if (existing) {
        return existing;
    }
    const type = loadedTypes.get(key);
    return type ? createAppSetting(store, key, type) : undefined;
};

const loadAppSetting = (store: GlobalSettingInstance, key: LazyAppSettingKey) =>
    getOrAdd(getAppSettingLoads(store), key, async () => {
        try {
            const type = await loadAppSettingType(key);
            return getLoadedAppSetting(store, key) ?? createAppSetting(store, key, type);
        } catch (error) {
            getAppSettingLoads(store).delete(key);
            throw error;
        }
    });

const preloadAppSettingType = async (key: LazyAppSettingKey) => {
    try {
        await loadAppSettingType(key);
    } catch {
        // Nothing to report yet: the app's first useGlobalSetting call loads it again, and a failure there reaches the error boundary.
    }
};

/** Starts loading the settings stores an app uses. Called when navigation to the app starts, so they load with the app's code. */
export const preloadAppSettings = (appId: string) => {
    lazyAppSettingKeys.filter((key) => appSettingLoaders[key].apps.includes(appId)).forEach((key) => void preloadAppSettingType(key));
};

/** Loaded app settings stores follow the store's slots after a whole-store load (for example, the saved settings arriving). */
const syncLoadedAppSettings = (store: GlobalSettingInstance) => {
    getAppSettings(store).forEach((appSetting, key) => {
        const type = loadedTypes.get(key);
        if (type) {
            applySnapshot(appSetting, toAppSnapshot(type, store[key]));
        }
    });
};

export function getInitialSettings(settingStorageKey: string) {
    const store = GlobalSettingStore.create();
    const settingData = localStorage.getItem(settingStorageKey);
    applySnapshotToStore(store, settingData);

    return store;
}

function applyGlobalSnapshotToStore(store: GlobalSettingInstance, setting: GlobalSettingInstance) {
    if (TimeStore.is(setting.time)) {
        applySnapshot(store.time, setting.time);
    }
    if (SiteThemeStore.is(setting.theme)) {
        applySnapshot(store.theme, setting.theme);
    }
    if (ControlSettingStore.is(setting.controlSetting)) {
        applySnapshot(store.controlSetting, castToSnapshot(setting.controlSetting));
    }
}

// The app settings that are kept one by one when the saved settings no longer fit the whole store.
const appSettingsKeptOneByOne: readonly LazyAppSettingKey[] = [
    "customerHubApp",
    "teamsApp",
    "ic3SpogApp",
    "contactsApp",
    "rulesApp",
    "mpaApp",
    "signalHealthApp",
    "deploymentHubApp",
    "agencyHubApp"
];

function applyAppSnapshotToStore(store: GlobalSettingInstance, setting: GlobalSettingInstance) {
    if (UserDashboardSettingsStore.is(setting.dashboardApp)) {
        applySnapshot(store.dashboardApp, setting.dashboardApp);
    }
    if (UserAppHubSettings.is(setting.appHubApp)) {
        applySnapshot(store.appHubApp, setting.appHubApp);
    }
    if (OpsGenieAppSetting.is(setting.opsGenieApp)) {
        applySnapshot(store.opsGenieApp, setting.opsGenieApp);
    }
    if (UserSearchSettings.is(setting.userSearchSettings)) {
        applySnapshot(store.userSearchSettings, setting.userSearchSettings);
    }
    appSettingsKeptOneByOne.forEach((key) => {
        const saved = setting[key];
        const type = loadedTypes.get(key);
        // An app whose store hasn't loaded is checked against its model when it loads.
        if (saved !== undefined && (!type || type.is(saved))) {
            applyPatch(store, { op: "replace", path: `/${key}`, value: saved });
        }
    });
}

export function applySnapshotToStore(store: GlobalSettingInstance, settingData: string | null | undefined) {
    if (!settingData) {
        return;
    }
    try {
        const json: GlobalSettingInstance = JSON.parse(settingData);

        if (GlobalSettingStore.is(json)) {
            applySnapshot(store, json);
        } else {
            // some schema has changed. second chance
            const setting: GlobalSettingInstance = json;
            applyGlobalSnapshotToStore(store, setting);
            applyAppSnapshotToStore(store, setting);
        }
    } catch {
        // ignore the error. do nothing when there is an exception.
    }
    syncLoadedAppSettings(store);
}

type GlobalSettingValues = { [K in storeNames]: K extends LazyAppSettingKey ? Instance<LazyAppSettingType<K>> : GlobalSettingInstance[K] };

const isGlobalSettingValue = <T extends storeNames>(key: T, value: unknown): value is GlobalSettingValues[T] =>
    isStateTreeNode(value) && (!isLazyAppSettingKey(key) || getType(value) === loadedTypes.get(key));

/**
 * A global settings store. App settings stores (see AppSettingLoaders.ts) load with their app; the first call before one has
 * loaded suspends until it has, like the app's own lazy code.
 */
export const useGlobalSetting = <T extends storeNames>(store: T): GlobalSettingValues[T] => {
    const globalSetting = useRootStore("GlobalSetting");
    const value: unknown = isLazyAppSettingKey(store)
        ? (getLoadedAppSetting(globalSetting, store) ?? use(loadAppSetting(globalSetting, store)))
        : globalSetting[store];
    if (!isGlobalSettingValue(store, value)) {
        const actual = isStateTreeNode(value) ? `a "${getType(value).name}" store` : typeof value;
        const expected = isLazyAppSettingKey(store) ? `the "${loadedTypes.get(store)?.name ?? store}" store from AppSettingLoaders.ts` : "a settings store";
        throw new Error(`useGlobalSetting("${store}") expected ${expected} but got ${actual}`);
    }
    return value;
};
